import type { Layer } from "@/domain/types";
import { inertCommandRows } from "@/games/sc2/sc2-ingest";
import type { ImportedKeyboard } from "@/hardware/keyboards/keyboard-import";
import { isPlaceholderCode } from "@/hardware/keyboards/kle-import";
import { type ImportedProfile, profileBindingCount, sc2BindingCounts } from "./profile-import";

/** One stat chip in the import preview. */
export interface PreviewStat {
  n: string | number;
  label: string;
  tone?: "ctrl";
}

/** Everything the preview pane renders, precomputed. Later import kinds build the same shape. */
export interface ImportPreview {
  heading: string;
  fileName: string | null;
  stats: PreviewStat[];
  coverage: { bound: number; total: number; label: string } | null;
  note: string;
}

/** Keys the importer could not map to a position (placeholder codes); they render as plain caps. */
export function countUnmappedKeys(keyboard: ImportedKeyboard["keyboard"]): number {
  return keyboard.keys.filter((k) => isPlaceholderCode(k.code)).length;
}

/** Preview for an imported Keyboard against the active resolved modifier layer of the current Profile. */
export function buildKeyboardPreview(
  imported: ImportedKeyboard,
  activeModifierLayer: Layer,
  fileName?: string,
): ImportPreview {
  const { keyboard, boardLayers, format } = imported;
  // physical keys, not unique codes: several keys can share a code and each renders its Binding
  const bound = keyboard.keys.filter((k) => activeModifierLayer[k.code] != null).length;
  const stats: PreviewStat[] = [
    { n: keyboard.keys.length, label: keyboard.keys.length === 1 ? "key" : "keys" },
    { n: `${keyboard.width}×${keyboard.height}`, label: "grid" },
    { n: bound, label: bound === 1 ? "key bound" : "keys bound", tone: "ctrl" },
  ];
  if (boardLayers.length > 1) {
    stats.splice(2, 0, { n: boardLayers.length, label: "board layers" });
  }
  let note: string;
  if (format === "kle") {
    const unmapped = countUnmappedKeys(keyboard);
    note =
      unmapped > 0
        ? `${unmapped} ${unmapped === 1 ? "key" : "keys"} couldn't be matched to a position — shown as plain caps.`
        : "Every key was recognized.";
  } else {
    const hidden = boardLayers.filter((l) => l.empty).length;
    note =
      hidden > 0
        ? `${hidden} all-transparent ${hidden === 1 ? "layer is" : "layers are"} hidden from the selector. Layer-switch and blank keys render as plain caps.`
        : "Layer-switch and blank keys render as plain caps.";
  }
  return {
    heading: `Keyboard — ${format.toUpperCase()}`,
    fileName: fileName ?? null,
    stats,
    coverage: { bound, total: keyboard.keys.length, label: keyboard.name },
    note,
  };
}

/** Preview for an uploaded simple-Game (Destiny) Profile, ingested through its own per-Game path. */
function buildDestinyProfilePreview(
  imported: Extract<ImportedProfile, { format: "destiny-cvars" }>,
  fileName?: string,
): ImportPreview {
  const { profile } = imported;
  const ctxIds = Object.keys(profile.contexts ?? {});
  const allBinds = [
    ...profile.base.flat(),
    ...ctxIds.flatMap((id) => profile.contexts?.[id] ?? []),
  ];
  const tokens = new Set(allBinds.map((cb) => cb.token));
  const modBinds = allBinds.filter((cb) => cb.combo !== "").length;
  const stats: PreviewStat[] = [
    { n: profileBindingCount(profile), label: "actions" },
    { n: ctxIds.length, label: ctxIds.length === 1 ? "context" : "contexts" },
    { n: tokens.size, label: "keys bound", tone: "ctrl" },
  ];
  if (modBinds > 0) stats.push({ n: modBinds, label: "modifier binds" });
  return {
    heading: "Profile — Destiny 2",
    fileName: fileName ?? null,
    stats,
    coverage: null,
    note: "Ingested from cvars.xml. Switch the Keyboard Layout if keys look misplaced after applying.",
  };
}

/** Names the dead per-unit lines (up to three) so a hand-written rebind doesn't vanish unexplained. */
function inertRowsSentence(text: string): string {
  const rows = inertCommandRows(text);
  if (rows.length === 0) return "";
  const named =
    rows.slice(0, 3).join(", ") + (rows.length > 3 ? ` and ${rows.length - 3} more` : "");
  const lead = rows.length === 1 ? "One per-unit line" : `${rows.length} per-unit lines`;
  const what = rows.length === 1 ? "this command" : "these commands";
  return ` ${lead} won't render (keylens models ${what} as one shared binding): ${named}.`;
}

/**
 * Preview for an uploaded Profile, dispatched per export format (SC2 [Hotkeys] decode or Destiny cvars).
 *
 * The SC2 stats lead with what the file BINDS, then split it. The headline used to be the [Hotkeys]
 * flatten's positional cell count, labelled "keys bound" and highlighted - so a real setup that
 * rebinds well over a thousand commands announced itself as "35 keys bound", the flatten's share
 * alone. The board still shows one command card at a time, but the import is of the whole file, and
 * that is what the confirmation must size.
 */
export function buildProfilePreview(imported: ImportedProfile, fileName?: string): ImportPreview {
  if (imported.format === "destiny-cvars") return buildDestinyProfilePreview(imported, fileName);
  const c = sc2BindingCounts(imported.table);
  return {
    heading: "Profile — StarCraft II",
    fileName: fileName ?? null,
    stats: [
      { n: c.total, label: "bindings", tone: "ctrl" },
      { n: c.global, label: "global" },
      { n: c.card, label: "command card" },
      { n: c.controlGroups, label: "control groups" },
      { n: c.modifier, label: "modifier binds" },
    ],
    coverage: null,
    note: `Parsed through QWERTY. If keys look misplaced after applying, switch the Keyboard Layout to re-parse.${inertRowsSentence(imported.text)}`,
  };
}
